import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { BannerContext } from '../../contexts/BannerContext';
import { useBilling } from '../../hooks/useBilling';
import { FLASH_SALE_CAMPAIGN } from '../../data/flashSale/campaignConfig';
import {
  buildRegisterUrl,
  buildExpiredFallbackUrl,
  isCampaignPurchasable,
  markCheckoutIntent,
  persistCampaignAttribution,
} from '../../data/flashSale/campaignHelpers';
import { setBillingCheckoutReturn } from '../billing/billingPlanData';
import analyticsService from '../../services/analyticsService';

/**
 * Paid flash-sale checkout (not free-trial onboarding).
 * Carries campaign_id so the server can apply the Stripe promotion code.
 */
export function useFlashSaleCheckout(campaign = FLASH_SALE_CAMPAIGN) {
  const { user } = useContext(AuthContext);
  const { showMessage } = useContext(BannerContext);
  const navigate = useNavigate();
  const { createCheckoutSession, loading } = useBilling();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const startCheckout = useCallback(
    async ({ source = 'pricing_cta' } = {}) => {
      setError(null);
      persistCampaignAttribution(campaign);

      if (!isCampaignPurchasable(campaign)) {
        analyticsService.trackTradingEvent('flash_sale_campaign_expired', {
          campaign_id: campaign.id,
          source,
        });
        navigate(buildExpiredFallbackUrl(campaign));
        return;
      }

      analyticsService.trackTradingEvent('flash_sale_pricing_cta_click', {
        campaign_id: campaign.id,
        source,
        plan_tier: campaign.offer.planTier,
        billing_interval: campaign.offer.billingInterval,
      });

      if (!user) {
        markCheckoutIntent();
        analyticsService.trackTradingEvent('flash_sale_signup_started', {
          campaign_id: campaign.id,
          source,
        });
        navigate(buildRegisterUrl(campaign));
        return;
      }

      setBusy(true);
      try {
        analyticsService.trackTradingEvent('flash_sale_checkout_started', {
          campaign_id: campaign.id,
          source,
          plan_tier: campaign.offer.planTier,
          billing_interval: campaign.offer.billingInterval,
        });

        setBillingCheckoutReturn({
          change: 'subscribe',
          targetTier: campaign.offer.planTier,
          source: 'flash-sale',
          returnPath: '/flash-sale',
        });

        await createCheckoutSession(campaign.offer.billingInterval, false, {
          planTier: campaign.offer.planTier,
          cancelReturn: 'flash-sale',
          campaignId: campaign.id,
        });
      } catch (e) {
        const message =
          e?.message || 'Could not start checkout. Please try again.';
        setError(message);
        showMessage?.(message, 'danger');
        analyticsService.trackTradingEvent('flash_sale_checkout_failed', {
          campaign_id: campaign.id,
          source,
          error: message,
        });
      } finally {
        setBusy(false);
      }
    },
    [campaign, createCheckoutSession, navigate, showMessage, user]
  );

  return {
    startCheckout,
    busy: busy || loading,
    error,
    isAuthenticated: Boolean(user),
  };
}
